/**
 * Hiding Shopify's express / accelerated checkout buttons.
 *
 * Shop Pay, Apple Pay, Google Pay and the offsite gateway buttons go straight to Shopify's own
 * checkout, so they would silently bypass this product (`PROMPT.md`, "Storefront integration"),
 * and Shopify's checkout is never a fallback (D385). The script hides them from the first paint
 * and they stay hidden unless the config answered `enabled: false`: an unreadable config keeps
 * them hidden, and a `hideExpressButtons: false` answer is ignored (`peak-checkout.ts`).
 *
 * Element names verified against source, not memory:
 *
 * - `<div data-shopify="payment-button" class="shopify-payment-button">` wrapping
 *   `<shopify-accelerated-checkout>` is the documented output of the `payment_button` filter —
 *   https://shopify.dev/docs/api/liquid/filters/payment_button (the "Output" panel).
 * - `shopify-accelerated-checkout` and `shopify-accelerated-checkout-cart` are styled by name
 *   in Dawn's `assets/base.css` under the comment "Shopify Accelerated Checkout"
 *   (github.com/Shopify/dawn, `main`, read 2026-09-14).
 * - `.additional-checkout-buttons` and `.cart__dynamic-checkout-buttons` wrap
 *   `content_for_additional_checkout_buttons` in Dawn's `sections/main-cart-footer.liquid`.
 * - `.dynamic-checkout__content` is targeted by Dawn's `assets/component-cart.css`
 *   (`.cart__dynamic-checkout-buttons:has(.dynamic-checkout__content:empty)`), and
 *   `#dynamic-checkout-cart` / `[data-shopify-buttoncontainer]` by
 *   `assets/component-cart-drawer.css`.
 * - `[data-shopify="dynamic-checkout-cart"]` is the attribute on the container Shopify injects
 *   into the cart at runtime. It is NOT shown in the public Liquid reference — Dawn styles the
 *   same node by its `#dynamic-checkout-cart` id — so both spellings are listed.
 */

/** Marks our one injected stylesheet so it can be found and removed again. */
export const PEAK_STYLE_ATTRIBUTE = 'data-peak-checkout-styles';

/** Everything that renders an express / accelerated checkout button. */
export const PEAK_EXPRESS_SELECTORS: readonly string[] = [
  '.shopify-payment-button',
  'shopify-accelerated-checkout',
  'shopify-accelerated-checkout-cart',
  '.additional-checkout-buttons',
  '.cart__dynamic-checkout-buttons',
  '.dynamic-checkout__content',
  '#dynamic-checkout-cart',
  '[data-shopify-buttoncontainer]',
  '[data-shopify="dynamic-checkout-cart"]',
  '[data-shopify="payment-button"]',
  // UpCart's drawer (`./cart-apps.ts`, upcart-2-413): its express container and its own wallet
  // buttons. Its legacy `#upcart-additional-checkout-buttons` also carries
  // `.additional-checkout-buttons`, matched above.
  '#upcart-express-payments',
  '#upcart-express-pay-buttons-container',
  '.upcart-express-pay-button',
];

/**
 * The stylesheet body.
 *
 * `display:none !important` rather than removing the nodes: Shopify's own scripts re-render
 * these containers, and a rule outlasts a re-render where a `remove()` would have to be
 * repeated by a `MutationObserver`.
 */
export function expressHideCss(): string {
  return PEAK_EXPRESS_SELECTORS.join(',') + '{display:none !important;}';
}

/** The part of `Document` this module needs, so the logic is testable without a DOM. */
export type YcDocumentLike = {
  querySelector(selector: string): { remove(): void } | null;
  createElement(tagName: string): {
    setAttribute(name: string, value: string): void;
    textContent: string | null;
  };
  head: { appendChild(node: unknown): unknown } | null;
  documentElement: { appendChild(node: unknown): unknown } | null;
};

const STYLE_SELECTOR = 'style[' + PEAK_STYLE_ATTRIBUTE + ']';

/** Inject the stylesheet, at most once. */
export function injectExpressStyle(doc: YcDocumentLike): void {
  if (doc.querySelector(STYLE_SELECTOR) !== null) return;

  const style = doc.createElement('style');
  style.setAttribute(PEAK_STYLE_ATTRIBUTE, '');
  style.textContent = expressHideCss();

  // `head` is present for the documented install (the snippet renders before `</body>`), but a
  // merchant who moves the snippet into `<head>` must not get a crash.
  const parent = doc.head !== null ? doc.head : doc.documentElement;
  if (parent !== null) parent.appendChild(style);
}

/** Remove the stylesheet — the store is switched off. */
export function removeExpressStyle(doc: YcDocumentLike): void {
  const existing = doc.querySelector(STYLE_SELECTOR);
  if (existing !== null) existing.remove();
}

/** Apply or undo the hiding, to match the resolved config. */
export function applyExpressPolicy(doc: YcDocumentLike, hide: boolean): void {
  if (hide) injectExpressStyle(doc);
  else removeExpressStyle(doc);
}
